// Chemin: C:\gestion-copro\src\pages\admin\ConseilSyndicalPage.tsx
// Session 57 : Ajout bouton "Lier un compte" pour créer les accès utilisateurs
// Session 58 : Correction bug coproprieteId (était coproprietaireId → maintenant coproprieteIds[0])
// Session 74 : Ajout bouton "Réinitialiser et renvoyer" les identifiants par email + modale
// Session 82 : Synchronisation email Auth/Firestore lors de la modification
// Session 89 : Ajout section désignation coordinateur + création mandat (Gouvernance V2)
// Session 90 : Envoi automatique des notifications email aux membres CS après création mandat

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  UserCheck,
  UserX,
  Crown,
  Mail,
  Phone,
  Link as LinkIcon,
  RefreshCw,
  AlertTriangle,
  ShieldCheck,
  FileSignature,
  Loader2
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { 
  getMembresCSByCopropriete, 
  createMembreCS, 
  updateMembreCS, 
  deleteMembreCS,
  desactiverMembreCS
} from '../../services/conseilSyndical.service';
import { getLotsResume } from '../../services/lot.service';
import { updateUserEmail } from '../../services/user.service';
import { 
  MembreCS, 
  MembreCSCreateData, 
  RoleCS, 
  ROLE_CS_LABELS 
} from '../../types/conseilSyndical.types';
import { LotResume } from '../../types/lot.types';
import LierCompteModal from '../../components/conseil-syndical/LierCompteModal';
import { useCopropriete } from '../../context/CoproprieteContext';
import { createMandat, getMandatActif, envoyerNotificationMandat } from '../../services/mandat.service';
import { MandatCoordination, STATUT_MANDAT_LABELS, STATUT_MANDAT_COLORS } from '../../types/mandat.types';

// Formulaire vide par défaut
const emptyFormData = {
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  lotId: '',
  lotNumero: '',
  role: 'membre' as RoleCS,
  dateElection: '',
  notes: ''
};

const ConseilSyndicalPage: React.FC = () => {
  const { userProfile } = useAuth();
  
  // ✅ CORRECTION Session 58 : coproprieteIds est un tableau, pas coproprietaireId
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // États
  const [membres, setMembres] = useState<MembreCS[]>([]);
  const [lots, setLots] = useState<LotResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactifs, setShowInactifs] = useState(false);
  
  // Modal création/édition membre
  const [showModal, setShowModal] = useState(false);
  const [editingMembre, setEditingMembre] = useState<MembreCS | null>(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [saving, setSaving] = useState(false);
  
  // Modal suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [membreToDelete, setMembreToDelete] = useState<MembreCS | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Modal lier compte
  const [showLierCompteModal, setShowLierCompteModal] = useState(false);
  const [membreToLink, setMembreToLink] = useState<MembreCS | null>(null);

  // Modal réinitialisation identifiants
  const [showResetModal, setShowResetModal] = useState(false);
  const [membreToReset, setMembreToReset] = useState<MembreCS | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState<{ success: boolean; message: string } | null>(null);

  // Session 89 : Section désignation coordinateur
  const { copropriete, refreshCopropriete } = useCopropriete();
  const [showDesignationSection, setShowDesignationSection] = useState(false);
  const [selectedCoordinateurId, setSelectedCoordinateurId] = useState<string>('');
  const [estMembreCS, setEstMembreCS] = useState<boolean>(true);
  const [creatingMandat, setCreatingMandat] = useState(false);
  const [mandatActif, setMandatActif] = useState<MandatCoordination | null>(null);
  const [mandatLoading, setMandatLoading] = useState(true);

  // Chargement
  useEffect(() => {
    const loadData = async () => {
      if (!coproprieteId) return;
      
      try {
        setLoading(true);
        const [membresData, lotsData] = await Promise.all([
          getMembresCSByCopropriete(coproprieteId, !showInactifs),
          getLotsResume(coproprieteId)
        ]);
        setMembres(membresData);
        setLots(lotsData);
      } catch (error) {
        console.error('Erreur chargement:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [coproprieteId, showInactifs]);

  // Recharger les membres
  const reloadMembres = async () => {
    if (!coproprieteId) return;
    const data = await getMembresCSByCopropriete(coproprieteId, !showInactifs);
    setMembres(data);
  };

  // Session 89 : Charger le mandat actif
  useEffect(() => {
    const loadMandat = async () => {
      if (!coproprieteId) {
        setMandatLoading(false);
        return;
      }
      try {
        const mandat = await getMandatActif(coproprieteId);
        setMandatActif(mandat);
      } catch (err) {
        console.error('Erreur chargement mandat:', err);
      } finally {
        setMandatLoading(false);
      }
    };
    loadMandat();
  }, [coproprieteId]);

  // Session 89 : Créer le mandat de coordination
  const handleCreateMandat = async () => {
    if (!coproprieteId || !selectedCoordinateurId) return;
    
    const membreChoisi = membresActifsAvecCompte.find(m => m.id === selectedCoordinateurId);
    if (!membreChoisi) {
      alert('Veuillez sélectionner un membre valide.');
      return;
    }

    // Les membres CS qui devront signer = tous les actifs avec compte SAUF le coordinateur
    const membresSignataires = membres
      .filter(m => m.actif && m.userId && m.id !== selectedCoordinateurId);

    if (membresSignataires.length === 0) {
      alert('Il faut au moins un autre membre CS avec un compte lié pour créer le mandat. Le coordinateur ne signe pas son propre mandat.');
      return;
    }

    if (!window.confirm(
      `Désigner ${membreChoisi.prenom} ${membreChoisi.nom} comme coordinateur ?\n\n` +
      `Un mandat sera créé et envoyé pour signature à ${membresSignataires.length} membre(s) du CS.`
    )) {
      return;
    }

    setCreatingMandat(true);
    try {
      const mandatId = await createMandat({
        coproprieteId,
        coordinateurId: membreChoisi.userId!,
        coordinateurNom: `${membreChoisi.prenom} ${membreChoisi.nom}`,
        estMembreCS,
        membresCSIds: membresSignataires.map(m => m.id),
        nomCopropriete: copropriete?.nom || 'Copropriété',
      });

      // Session 90 : Envoyer les notifications email aux membres CS
      try {
        const notifResult = await envoyerNotificationMandat(coproprieteId, mandatId);
        if (notifResult.envoyes > 0) {
          alert(
            `Mandat créé avec succès !\n\n` +
            `${notifResult.envoyes} email(s) de notification envoyé(s) sur ${notifResult.total}.` +
            (notifResult.erreurs.length > 0 ? `\n\nErreurs :\n${notifResult.erreurs.join('\n')}` : '')
          );
        } else if (notifResult.total === 0) {
          alert('Mandat créé avec succès !\n\nAucun membre à notifier (pas de compte lié ou pas d\'email).');
        } else {
          alert(
            `Mandat créé mais les notifications ont échoué.\n\n` +
            `Erreurs :\n${notifResult.erreurs.join('\n')}`
          );
        }
      } catch (notifError: any) {
        console.error('Erreur envoi notifications mandat:', notifError);
        alert(
          'Mandat créé avec succès, mais l\'envoi des notifications a échoué.\n\n' +
          'Les membres CS devront être prévenus manuellement.'
        );
      }

      // Recharger le mandat et rafraîchir la copropriété
      const mandat = await getMandatActif(coproprieteId);
      setMandatActif(mandat);
      await refreshCopropriete();
      setShowDesignationSection(false);
      setSelectedCoordinateurId('');
    } catch (error: any) {
      console.error('Erreur création mandat:', error);
      alert(`Erreur : ${error.message || 'Impossible de créer le mandat'}`);
    } finally {
      setCreatingMandat(false);
    }
  };

  // Ouvrir modal création
  const handleCreate = () => {
    setEditingMembre(null);
    setFormData(emptyFormData);
    setShowModal(true);
  };

  // Ouvrir modal édition
  const handleEdit = (membre: MembreCS) => {
    setEditingMembre(membre);
    
    // Formater la date pour l'input date
    let dateStr = '';
    if (membre.dateElection) {
      const date = membre.dateElection.toDate();
      dateStr = date.toISOString().split('T')[0];
    }
    
    setFormData({
      nom: membre.nom,
      prenom: membre.prenom,
      email: membre.email || '',
      telephone: membre.telephone || '',
      lotId: membre.lotId || '',
      lotNumero: membre.lotNumero || '',
      role: membre.role,
      dateElection: dateStr,
      notes: membre.notes || ''
    });
    setShowModal(true);
  };

  // Ouvrir modal lier compte
  const handleLierCompte = (membre: MembreCS) => {
    setMembreToLink(membre);
    setShowLierCompteModal(true);
  };

  // Quand on sélectionne un lot, remplir automatiquement le numéro
  const handleLotChange = (lotId: string) => {
    const lot = lots.find(l => l.id === lotId);
    setFormData({
      ...formData,
      lotId,
      lotNumero: lot ? lot.numero : ''
    });
  };

  // Sauvegarder
  // Session 82 : Ajout synchronisation email Auth/Firestore si compte lié
  const handleSave = async () => {
    if (!coproprieteId || !formData.nom || !formData.prenom || !formData.dateElection) {
      alert('Veuillez remplir les champs obligatoires (nom, prénom, date élection)');
      return;
    }
    
    setSaving(true);
    
    try {
      const membreData: MembreCSCreateData = {
        coproprieteId,
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        role: formData.role,
        dateElection: new Date(formData.dateElection),
        actif: true
      };
      
      // Champs optionnels
      if (formData.email.trim()) membreData.email = formData.email.trim();
      if (formData.telephone.trim()) membreData.telephone = formData.telephone.trim();
      if (formData.lotId) membreData.lotId = formData.lotId;
      if (formData.lotNumero) membreData.lotNumero = formData.lotNumero;
      if (formData.notes.trim()) membreData.notes = formData.notes.trim();
      
      if (editingMembre) {
        // Session 82 : Vérifier si l'email a changé ET si un compte est lié
        const emailChanged = formData.email.trim() !== (editingMembre.email || '');
        const hasLinkedAccount = !!editingMembre.userId;
        
        if (emailChanged && hasLinkedAccount && formData.email.trim()) {
          // Synchroniser l'email avec Firebase Auth via Cloud Function
          try {
            await updateUserEmail({
              userId: editingMembre.userId!,
              newEmail: formData.email.trim()
            });
            // La Cloud Function met à jour users + membresCS, pas besoin de updateMembreCS pour l'email
            // Mais on doit quand même mettre à jour les autres champs
            await updateMembreCS(editingMembre.id, {
              nom: membreData.nom,
              prenom: membreData.prenom,
              // email est déjà mis à jour par la Cloud Function
              telephone: membreData.telephone,
              lotId: membreData.lotId,
              lotNumero: membreData.lotNumero,
              role: membreData.role,
              dateElection: membreData.dateElection,
              notes: membreData.notes
            });
          } catch (emailError: any) {
            console.error('Erreur synchronisation email:', emailError);
            // Si erreur spécifique, afficher un message clair
            if (emailError.code === 'functions/already-exists') {
              alert('Cette adresse email est déjà utilisée par un autre compte.');
            } else {
              alert(`Erreur lors de la mise à jour de l'email : ${emailError.message || 'Erreur inconnue'}`);
            }
            setSaving(false);
            return;
          }
        } else {
          // Pas de compte lié ou email inchangé : mise à jour normale
          await updateMembreCS(editingMembre.id, {
            nom: membreData.nom,
            prenom: membreData.prenom,
            email: membreData.email,
            telephone: membreData.telephone,
            lotId: membreData.lotId,
            lotNumero: membreData.lotNumero,
            role: membreData.role,
            dateElection: membreData.dateElection,
            notes: membreData.notes
          });
        }
      } else {
        await createMembreCS(membreData);
      }
      
      // Recharger
      await reloadMembres();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde membre CS:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Désactiver un membre
  const handleDesactiver = async (membre: MembreCS) => {
    if (!coproprieteId) return;
    
    if (!window.confirm(`Désactiver ${membre.prenom} ${membre.nom} du conseil syndical ?`)) {
      return;
    }
    
    try {
      await desactiverMembreCS(membre.id, new Date());
      await reloadMembres();
    } catch (error) {
      console.error('Erreur désactivation:', error);
      alert('Erreur lors de la désactivation');
    }
  };

  // Réactiver un membre
  const handleReactiver = async (membre: MembreCS) => {
    if (!coproprieteId) return;
    
    try {
      await updateMembreCS(membre.id, { actif: true });
      await reloadMembres();
    } catch (error) {
      console.error('Erreur réactivation:', error);
      alert('Erreur lors de la réactivation');
    }
  };

  // Ouvrir modal réinitialisation
  const handleResetClick = (membre: MembreCS) => {
    setMembreToReset(membre);
    setResetResult(null);
    setShowResetModal(true);
  };

  // Confirmer réinitialisation
  const handleResetConfirm = async () => {
    if (!membreToReset?.userId || !membreToReset?.email || !coproprieteId) {
      return;
    }

    setResetting(true);
    setResetResult(null);

    try {
      const resetAndResend = httpsCallable(functions, 'resetAndResendCredentials');
      const result = await resetAndResend({ 
        userId: membreToReset.userId,
        coproprieteId: coproprieteId
      });
      
      const data = result.data as { success: boolean; message: string; emailSent: boolean };
      
      setResetResult({
        success: data.success && data.emailSent,
        message: data.message
      });
    } catch (error: any) {
      console.error('Erreur réinitialisation:', error);
      setResetResult({
        success: false,
        message: error.message || 'Erreur lors de la réinitialisation'
      });
    } finally {
      setResetting(false);
    }
  };

  // Fermer modal réinitialisation
  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setMembreToReset(null);
    setResetResult(null);
  };

  // Confirmer suppression
  const handleDeleteClick = (membre: MembreCS) => {
    setMembreToDelete(membre);
    setShowDeleteConfirm(true);
  };

  // Supprimer
  const handleDeleteConfirm = async () => {
    if (!membreToDelete || !coproprieteId) return;
    
    setDeleting(true);
    
    try {
      await deleteMembreCS(membreToDelete.id);
      await reloadMembres();
      setShowDeleteConfirm(false);
      setMembreToDelete(null);
    } catch (error) {
      console.error('Erreur suppression membre CS:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Icône du rôle
  const getRoleIcon = (role: RoleCS) => {
    if (role === 'president') {
      return <Crown className="w-4 h-4 text-amber-500" />;
    }
    return null;
  };

  if (!coproprieteId) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Aucune copropriété associée à votre compte.</p>
      </div>
    );
  }

  const membresActifs = membres.filter(m => m.actif);
  const membresInactifs = membres.filter(m => !m.actif);

  // Session 89 : Membres éligibles pour la coordination (actifs + compte lié)
  const membresActifsAvecCompte = membresActifs.filter(m => m.userId);
  const hasCoordinateur = !!copropriete?.coordinateurId;
  const hasMandatEnCours = mandatActif && (mandatActif.statut === 'en_attente_signatures' || mandatActif.statut === 'actif');

  return (
    <div className="p-6">
      {/* Session 89 : Section Coordinateur / Mandat */}
      {!mandatLoading && (
        <div className="mb-6">
          {/* Cas 1 : Mandat actif ou en attente — afficher le statut */}
          {hasMandatEnCours && mandatActif && (
            <div className={`rounded-xl border p-5 ${
              mandatActif.statut === 'actif' 
                ? 'bg-green-50 border-green-200' 
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    mandatActif.statut === 'actif' ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {mandatActif.statut === 'actif' ? (
                      <ShieldCheck className="text-green-600" size={20} />
                    ) : (
                      <FileSignature className="text-amber-600" size={20} />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Coordinateur désigné</h3>
                    <p className="text-sm text-gray-600">
                      {mandatActif.coordinateurNom}
                      {mandatActif.estMembreCS && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                          Membre CS
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  STATUT_MANDAT_COLORS[mandatActif.statut]
                }`}>
                  {STATUT_MANDAT_LABELS[mandatActif.statut]}
                </span>
              </div>
            </div>
          )}

          {/* Cas 2 : Pas de mandat — proposer la désignation */}
          {!hasMandatEnCours && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5">
              {!showDesignationSection ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <ShieldCheck className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Aucun coordinateur désigné</h3>
                      <p className="text-sm text-gray-500">
                        Désignez un membre du CS pour piloter la copropriété sur Copro Protect.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDesignationSection(true)}
                    disabled={membresActifsAvecCompte.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Crown className="w-4 h-4" />
                    Désigner
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Crown className="text-blue-600" size={20} />
                    </div>
                    <h3 className="font-semibold text-gray-900">Désigner le coordinateur</h3>
                  </div>

                  {membresActifsAvecCompte.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="text-amber-600 flex-shrink-0" size={16} />
                        <p className="text-sm text-amber-800">
                          Aucun membre actif n'a de compte lié. Créez d'abord les comptes via le bouton 
                          <LinkIcon className="inline w-3 h-3 mx-1" /> sur chaque membre.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Sélection du coordinateur */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Membre désigné comme coordinateur
                        </label>
                        <select
                          value={selectedCoordinateurId}
                          onChange={(e) => setSelectedCoordinateurId(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">-- Sélectionner un membre --</option>
                          {membresActifsAvecCompte.map(m => (
                            <option key={m.id} value={m.id}>
                              {m.prenom} {m.nom} — {ROLE_CS_LABELS[m.role]}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Question membre CS élu */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ce coordinateur est-il membre élu du conseil syndical ?
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="estMembreCS"
                              checked={estMembreCS === true}
                              onChange={() => setEstMembreCS(true)}
                              className="text-blue-600"
                            />
                            <span className="text-sm text-gray-700">Oui, membre élu</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="estMembreCS"
                              checked={estMembreCS === false}
                              onChange={() => setEstMembreCS(false)}
                              className="text-blue-600"
                            />
                            <span className="text-sm text-gray-700">Non, personne externe</span>
                          </label>
                        </div>
                      </div>

                      {/* Info sur le workflow */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          Un mandat de coordination sera créé. Les autres membres du CS devront le signer 
                          lors de leur prochaine connexion pour valider la désignation.
                        </p>
                      </div>

                      {/* Boutons */}
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => {
                            setShowDesignationSection(false);
                            setSelectedCoordinateurId('');
                          }}
                          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleCreateMandat}
                          disabled={!selectedCoordinateurId || creatingMandat}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          {creatingMandat ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Création...
                            </>
                          ) : (
                            <>
                              <FileSignature className="w-4 h-4" />
                              Créer le mandat
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conseil Syndical</h1>
          <p className="text-gray-600 mt-1">
            {membresActifs.length} membre{membresActifs.length > 1 ? 's' : ''} actif{membresActifs.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ajouter un membre
        </button>
      </div>

      {/* Toggle membres inactifs */}
      {membresInactifs.length > 0 && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactifs}
              onChange={(e) => setShowInactifs(e.target.checked)}
              className="rounded"
            />
            Afficher les anciens membres ({membresInactifs.length})
          </label>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Chargement...</div>
      ) : membres.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun membre du conseil syndical</p>
          <button
            onClick={handleCreate}
            className="mt-4 text-blue-600 hover:underline"
          >
            Ajouter le premier membre
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {membres.map((membre) => (
            <div
              key={membre.id}
              className={`bg-white rounded-lg shadow p-4 ${!membre.actif ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${membre.actif ? 'bg-blue-600' : 'bg-gray-400'}`}>
                    {membre.prenom.charAt(0)}{membre.nom.charAt(0)}
                  </div>
                  
                  {/* Infos */}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {membre.prenom} {membre.nom}
                      </h3>
                      {getRoleIcon(membre.role)}
                      {!membre.actif && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Inactif
                        </span>
                      )}
                      {membre.userId ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <UserCheck className="w-3 h-3" />
                          Compte lié
                        </span>
                      ) : (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                          Sans compte
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-blue-600 font-medium">
                      {ROLE_CS_LABELS[membre.role]}
                    </p>
                    
                    {membre.lotNumero && (
                      <p className="text-sm text-gray-500 mt-1">
                        Lot {membre.lotNumero}
                      </p>
                    )}
                    
                    <div className="flex gap-4 mt-2">
                      {membre.email && (
                        <a
                          href={`mailto:${membre.email}`}
                          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                        >
                          <Mail className="w-3 h-3" />
                          {membre.email}
                        </a>
                      )}
                      {membre.telephone && (
                        <a
                          href={`tel:${membre.telephone}`}
                          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                        >
                          <Phone className="w-3 h-3" />
                          {membre.telephone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  {/* Bouton Lier un compte - visible si pas de userId et membre actif */}
                  {membre.actif && !membre.userId && (
                    <button
                      onClick={() => handleLierCompte(membre)}
                      className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Créer un compte"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  )}

                  {/* Bouton Réinitialiser et renvoyer - visible si userId existe et membre actif */}
                  {membre.actif && membre.userId && (
                    <button
                      onClick={() => handleResetClick(membre)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      title="Réinitialiser et renvoyer les identifiants"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  
                  {membre.actif ? (
                    <button
                      onClick={() => handleDesactiver(membre)}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Désactiver"
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactiver(membre)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Réactiver"
                    >
                      <UserCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(membre)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(membre)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingMembre ? 'Modifier le membre' : 'Nouveau membre'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Nom et Prénom */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle au conseil syndical *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as RoleCS })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.entries(ROLE_CS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              {/* Lot */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot possédé
                </label>
                <select
                  value={formData.lotId}
                  onChange={(e) => handleLotChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Sélectionner un lot --</option>
                  {lots.map((lot) => (
                    <option key={lot.id} value={lot.id}>
                      {lot.numero} - {lot.proprietaireNom}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Date élection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date d'élection *
                </label>
                <input
                  type="date"
                  value={formData.dateElection}
                  onChange={(e) => setFormData({ ...formData, dateElection: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Contact */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {/* Session 82 : Avertissement si compte lié */}
                    {editingMembre?.userId && formData.email !== (editingMembre.email || '') && (
                      <p className="mt-1 text-xs text-amber-600">
                        ⚠️ La modification de l'email mettra aussi à jour les identifiants de connexion.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes internes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Informations complémentaires..."
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmation suppression */}
      {showDeleteConfirm && membreToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Confirmer la suppression
            </h2>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{membreToDelete.prenom} {membreToDelete.nom}</strong> du conseil syndical ?
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setMembreToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Réinitialiser et renvoyer */}
      {showResetModal && membreToReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            {!resetResult ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Réinitialiser les identifiants
                  </h2>
                </div>
                
                <p className="text-gray-600 mb-4">
                  Un nouveau mot de passe sera généré et envoyé par email à <strong>{membreToReset.prenom} {membreToReset.nom}</strong>.
                </p>
                
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <p className="text-blue-700 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>{membreToReset.email}</span>
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCloseResetModal}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleResetConfirm}
                    disabled={resetting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {resetting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Envoyer
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${resetResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
                    {resetResult.success ? (
                      <Mail className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {resetResult.success ? 'Email envoyé' : 'Erreur'}
                  </h2>
                </div>
                
                <p className={`mb-6 ${resetResult.success ? 'text-gray-600' : 'text-red-600'}`}>
                  {resetResult.message}
                </p>

                <div className="flex justify-end">
                  <button
                    onClick={handleCloseResetModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Lier un compte */}
      {membreToLink && coproprieteId && (
        <LierCompteModal
          isOpen={showLierCompteModal}
          onClose={() => {
            setShowLierCompteModal(false);
            setMembreToLink(null);
          }}
          onSuccess={reloadMembres}
          membre={{
            id: membreToLink.id,
            nom: membreToLink.nom,
            prenom: membreToLink.prenom,
            email: membreToLink.email
          }}
          membreCollection="membresCS"
          coproprieteId={coproprieteId}
          role="cs"
        />
      )}
    </div>
  );
};

export default ConseilSyndicalPage;
